import React, { useState, useMemo } from 'react';
import {
  TouchableWithoutFeedback,
  GestureResponderEvent,
  View,
} from 'react-native';

import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import SSErrorBoundary from '../../SSErrorBoundary';
import {
  BodyText,
  Stack,
  AnimatedBadge,
} from '../../ui';

import { useJanitor } from '../../../hooks/useJanitor';
import { useMount } from '../../../hooks';
import { clickable } from '../../../styles';

/** Any component with a "color" property */
type ColorableIconType = React.JSXElementConstructor<{ color: string }>;
type NavBarItemStateProps = {
  /** The item ID, used for testing */
  id: string;
  /** True if the item has been selected, otherwise false */
  selected?: boolean;
  /** True if the item has been disabled, otherwise false */
  disabled?: boolean;
};
type IconProps = { color: string } & NavBarItemStateProps;
/**
 * A function which, given props and an Icon, renders the icon, including any desired wrapping
 * components
 * */
export type IconRenderer = (props: IconProps, Icon: ColorableIconType) => React.ReactNode;

type NavBarItemProps = NavBarItemStateProps & {
  /** Label for the item */
  label: string;
  /** Called when the item is selected */
  onSelect: (evt: GestureResponderEvent) => void;
  /** Selection color for an item */
  selectedColor?: string;
  /** Default color for an item */
  defaultColor?: string;
  /** Any additional components to render within the item */
  children?: React.ReactNode;
  /** True to show a non-numeric badge in the upper right of the item */
  showBadge?: boolean;
  /** True will make it show badge even when selected */
  showBadgeWhenSelected?: boolean;
  badgeAppearance?: 'client' | 'pro';
  /** Icon to render within the item */
  icon?: ColorableIconType;
  /** Alternate icon to render when the item is selected */
  selectedIcon?: ColorableIconType;
  /** A function which, given some props and the icon, returns a component containing the item */
  renderIcon?: IconRenderer;
};

/** Provides functionality for individual tabs in the pro and client side bottom nav bars */
const NavBarItem: React.FCWithChildren<NavBarItemProps> = props => {
  const {
    id,
    label,
    selected,
    onSelect,
    selectedColor = BrandColor.BackInBlack,
    defaultColor = BrandColor.Ash,
    children,
    disabled,
    showBadge,
    showBadgeWhenSelected = false,
    badgeAppearance,
    icon,
    selectedIcon,
    renderIcon,
  } = props;
  const [triggerAnimation, setTriggerAnimation] = useState<boolean>(false);
  const janitor = useJanitor();

  useMount(() => {
    /** need to give some time so animation is triggered after render */
    janitor.setTimeout(() => {
      setTriggerAnimation(true);
    }, 2500);
  });

  const color: string = useMemo<string>(() => {
    if (disabled) {
      return BrandColor.Silver;
    }

    if (selected) {
      return selectedColor;
    }

    return defaultColor;
  }, [
    disabled,
    selected,
    selectedColor,
    defaultColor,
  ]);

  const handlePress = (evt: GestureResponderEvent) => {
    evt.preventDefault();
    if (!disabled) {
      onSelect(evt);
    }
  };

  const renderedIcon: React.ReactNode = useMemo(() => {
    const Icon: ColorableIconType = (selected && selectedIcon) ? selectedIcon : icon;
    const iconColor: string = selected ? BrandColor.WhiteWash : color;
    let result: React.ReactNode = null;
    if (renderIcon) {
      result = renderIcon({ ...props, color: iconColor }, Icon);
    } else if (Icon) {
      result = (
        <Box
          color={selected ? selectedColor : BrandColor.WhiteWash}
          borderWidth={1}
          borderStyle="solid"
          borderColor={color}
          borderRadius={2}
          style={{ width: 20, height: 20 }}
          justifyContent="center"
          alignItems="center"
        >
          <Icon color={iconColor} />
        </Box>
      );
    }

    return result;
  }, [
    selected,
    disabled,
    renderIcon,
    icon,
    selectedIcon,
    color,
    selectedColor,
  ]);

  return (
    <TouchableWithoutFeedback onPress={handlePress}>
      <Stack
        testID={`pro-tab-${id}`}
        justifyContent="flex-end"
        alignItems="center"
        style={clickable}
      >
        <Stack paddingBottom={2} alignItems="center">
          {renderedIcon}
          {(showBadge && (!selected || showBadgeWhenSelected)) && (
            <View
              style={{
                position: 'absolute',
                right: -8,
                top: 0,
                justifyContent: 'center',
                alignItems: 'center',
              }}
              testID={`icon-badge-${badgeAppearance}`}
            >
              <AnimatedBadge show={triggerAnimation} appearance={badgeAppearance} />
            </View>
          )}
        </Stack>
        <BodyText appearance="smaller" color={color}>{label}</BodyText>
        <SSErrorBoundary info="Tabbar tab children">
          {children}
        </SSErrorBoundary>
      </Stack>
    </TouchableWithoutFeedback>
  );
};

NavBarItem.defaultProps = {
  selected: false,
  children: null,
  disabled: false,
  showBadge: false,
  badgeAppearance: undefined,
};

export default NavBarItem;
